import { getToken } from "./identity.js";

/** Thin fetch wrapper. Attaches the Netlify Identity JWT and unwraps API errors. */

async function authHeader() {
  const jwt = await getToken(); // refreshes itself when close to expiry
  return jwt ? { Authorization: `Bearer ${jwt}` } : {};
}

export class ApiError extends Error {
  constructor(status, message) {
    super(message);
    this.status = status;
  }
}

async function request(path, { method = "GET", body, form } = {}) {
  const headers = await authHeader();
  const init = { method, headers };

  if (form) init.body = form;
  else if (body !== undefined) {
    init.headers = { ...headers, "content-type": "application/json" };
    init.body = JSON.stringify(body);
  }

  const res = await fetch(path.startsWith("/") ? path : `/api/${path}`, init);
  const type = res.headers.get("content-type") || "";

  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    if (type.includes("application/json")) {
      const data = await res.json().catch(() => null);
      if (data?.error) message = data.error;
    }
    throw new ApiError(res.status, message);
  }

  if (res.status === 204) return null;
  return type.includes("application/json") ? await res.json() : await res.blob();
}

export const api = {
  get: (path) => request(path),
  post: (path, body) => request(path, { method: "POST", body }),
  put: (path, body) => request(path, { method: "PUT", body }),
  del: (path) => request(path, { method: "DELETE" }),
  upload: (path, file) => {
    const form = new FormData();
    form.append("file", file);
    return request(path, { method: "POST", form });
  },
  /**
   * Multipart POST for endpoints that take more than one file, or field names
   * other than `file` — build the FormData yourself and hand it over.
   */
  uploadForm: (path, form) => request(path, { method: "POST", form }),
  /** Public (unauthenticated) endpoints for the client-facing pages. */
  pub: {
    get: (path) => fetch(`/pub/${path}`).then(async (r) => {
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new ApiError(r.status, data.error || "Not available");
      return data;
    }),
    post: (path) => fetch(`/pub/${path}`, { method: "POST" }).then(async (r) => {
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new ApiError(r.status, data.error || "Not available");
      return data;
    }),
  },
  /**
   * Paths only — do NOT put these straight into an <img src> or an <a href>.
   *
   * These endpoints require the Identity bearer token, and a browser-initiated
   * image load or navigation sends no Authorization header, so it comes back 401
   * (a broken-image icon, or a blank tab). Fetch them through `blobUrl` or
   * `openBlob` below, which attach the token and hand the browser a local object
   * URL instead.
   */
  filePath: (key) => `/api/files?key=${encodeURIComponent(key)}`,
  invoicePdfPath: (id) => `/api/invoices/${id}/pdf`,
  estimatePdfPath: (id) => `/api/estimates/${id}/pdf`,

  /** Authenticated fetch → object URL, for <img>. Revoke it when you're done. */
  blobUrl: async (path) => {
    const blob = await request(path);
    return URL.createObjectURL(blob);
  },

  /**
   * Authenticated fetch → saved to disk. For the CSV exports: a plain <a href>
   * can't carry the bearer token, and openBlob would display the text instead
   * of downloading it.
   */
  download: async (path, filename) => {
    const blob = await request(path);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "export.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  },

  /**
   * Authenticated fetch → opens in a new tab. The tab is opened synchronously,
   * before the await, or popup blockers reject it.
   */
  openBlob: async (path) => {
    const tab = window.open("", "_blank");
    try {
      const blob = await request(path);
      const url = URL.createObjectURL(blob);
      if (tab) tab.location = url;
      else window.location.assign(url); // popup blocked: navigate instead of failing silently
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      tab?.close();
      throw err;
    }
  },
};
